<template>
  <Tile
    :name="name"
    icon="download-file"
    clickable
    :image="image"
    :dark-image-bg="composition.properties.darkBackground"
    :selected="selected"
    :is-selectable="isSelectable"
    :always-show-checkbox="alwaysShowCheckbox"
    :image-overlay-icon="overlayIcon"
    :display-status="displayPosStatus"
    class="publishing-tile"
    @on-click="edit"
    @toggle-selection="$emit('toggleSelection')"
  >
    <template v-if="designCount > 1" #overlay>
      <div class="overlay">
        <Badge type="neutral">
          {{
            `${$t('DESIGNS.CONFIGURATION.EDITOR.TAB_PRINTAREAS')}: ${designCount}`
          }}
        </Badge>
      </div>
    </template>
    <template v-if="displayPosStatus" #status>
      <CompositionPosStatusBadges
        class="badges"
        :composition="composition"
        :relevant-pos-types="relevantPosTypes"
        :relevant-pos-states="relevantPosStates"
        :hide-icon="relevantPosTypes.length <= 1"
      />
    </template>
    <template v-if="sellableCount !== null || showsDesignProductsCta" #sideinfo>
      <p v-if="sellableCount !== null" class="sellable-count">
        {{ $t('PUBLISHING.TILE.PRODUCT_COUNT', { count: sellableCount }) }}
      </p>
      <button
        v-if="showsDesignProductsCta"
        type="button"
        class="text-btn link-main design-products-cta"
        data-testid="publishing-tile-design-products-cta"
        @click="$emit('designProducts')"
      >
        {{ $t('PUBLISHING.DETAIL.HEADING') }}
        <Icon icon="chevron-small-right" />
      </button>
    </template>
  </Tile>
</template>

<script>
import compositionHelper from 'commons/composition/compositionHelper';
import {
  COMPOSITION_POS_STATES,
  getCompositionPosStateForPosType,
} from 'commons/api/models/publishingCore/compositionPosStates';
import Tile from 'commons/tileView/Tile.vue';
import CompositionPosStatusBadges from 'src/app/components/publishing/statusBadges/CompositionPosStatusBadges.vue';
import Badge from 'src/app/components/badge/Badge.vue';

export default {
  name: 'PublishingTile',
  components: {
    Badge,
    Tile,
    CompositionPosStatusBadges,
  },
  props: {
    composition: {
      required: true,
      type: Object,
    },
    displayPosStatus: {
      type: Boolean,
    },
    relevantPosTypes: {
      type: Array,
    },
    relevantPosStates: {
      type: Array,
    },
    /** Draws the tile as the current choice. For pickers — the list page never selects. */
    selected: {
      type: Boolean,
    },
    /**
     * Renders the checkbox that marks a tile as picked. Pass it with `selected` so the marking
     * matches the app's other selectable tile grids rather than relying on the ring alone.
     */
    isSelectable: {
      type: Boolean,
    },
    /** Shows the checkbox without hovering. What the other selectable grids do. */
    alwaysShowCheckbox: {
      type: Boolean,
    },
    /**
     * Rendered under the name as "N products". `null` omits the line — what the list page wants, and
     * all a caller of the plain composition list can pass (see `getCompositionsWithSellables`).
     */
    sellableCount: {
      type: Number,
      default: null,
    },
    /** The icon of the hover overlay. `null` keeps `Tile`'s pen, which reads as "edit". */
    overlayIcon: {
      type: String,
      default: null,
    },
    /**
     * Offers a "Design products" link while the composition is unpublished in every
     * `relevantPosTypes`. Pending and rejected ones get none: the partner already published those.
     */
    designProductsCta: {
      type: Boolean,
    },
  },
  emits: ['edit', 'designProducts', 'toggleSelection'],
  computed: {
    name() {
      return (
        compositionHelper.getMetaDataPart({
          composition: this.composition,
          part: 'name',
        })?.value || this.composition.id
      );
    },
    image() {
      return compositionHelper.getMainDesignImage(this.composition);
    },
    designCount() {
      return this.composition.designs?.length || 0;
    },
    showsDesignProductsCta() {
      return (
        this.designProductsCta &&
        !!this.relevantPosTypes?.length &&
        this.relevantPosTypes.every(
          (posType) =>
            getCompositionPosStateForPosType({
              composition: this.composition,
              posType,
            }).status.key === COMPOSITION_POS_STATES.UNPUBLISHED.key,
        )
      );
    },
  },
  methods: {
    edit() {
      this.$emit('edit');
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/styleguide/mixins/media';
@import 'src/scss/styleguide/type';

.publishing-tile {
  height: 100%;

  :deep(.card-footer) {
    height: auto;
  }

  .badges {
    margin: 12px 0 0 0;
  }

  .overlay {
    position: absolute;
    bottom: 8px;
    left: 8px;
    font-size: 0.75em;
  }

  .sellable-count {
    margin: 4px 0 0 0;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.5px;
    color: $grey65;
  }

  .design-products-cta {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    margin: 12px 0 0 8px;
    font-size: 14px;
    font-weight: bold;
    white-space: nowrap;

    .icon {
      width: 16px;
      height: 16px;
      transition: transform 0.15s ease-out;
    }
  }

  // Image and link lead to the same place, so hovering one shows the other's hover state.
  &:has(.image-wrapper:hover) .design-products-cta,
  .design-products-cta:hover {
    color: $pa-color-main-dark;

    .icon {
      transform: translateX(2px);
    }
  }

  &:has(.design-products-cta:hover) :deep(.image-overlay) {
    display: block;
  }
}
</style>
